import {
	createContext,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react'
import { Link, NavLink, useFetcher } from 'react-router'
import { OyAvatar } from '#app/components/oy/oy-avatar.tsx'
import { OyChip, OyChipGroup } from '#app/components/oy/oy-chip.tsx'
import { OyField } from '#app/components/oy/oy-field.tsx'
import {
	OyTabBar,
	TabMark,
	type OyTabItem,
} from '#app/components/oy/oy-nav.tsx'
import { useOyToast } from '#app/components/oy/oy-toast.tsx'
import { OyTray, OyTrayField, OyTrayNote } from '#app/components/oy/oy-tray.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { addDays } from '#app/utils/activity-presets.ts'
import { type LessonKind } from '#app/utils/lessons.server.ts'
import { cn } from '#app/utils/misc.tsx'
import {
	ARRIVAL_LEAD_MIN,
	arrivalFor,
	MOODS,
	NOTE_TAGS,
} from '#app/utils/stal-instructeur-fields.ts'
import {
	type InstructeurCheck,
	type InstructeurLessonRow,
	type InstructeurShell,
} from '#app/utils/stal-instructeur.server.ts'

/**
 * Herontwerp P7 — the instructor's chrome (StalAppRollen role=instructeur):
 * the header "Lessen {Else}" with his avatar, the two tabs (Lessen 🏇 ·
 * Voortgang 📈, no Overleg), and his trays: tijd, annuleer, les, nieuw,
 * afwezig, log. Every write posts one intent to /stal/instructeur/doorgeven;
 * the "Voor Else" notes are the day-plan's own anchor sentence (the brain's
 * wording, nothing composed here) and the annuleer tray shows the brain's
 * preview when it can compose one, otherwise the carer-facing consequence.
 */

export const INSTRUCTEUR_DOORGEVEN = '/stal/instructeur/doorgeven'

function tabs(badge: boolean): OyTabItem[] {
	return [
		{
			to: '/stal/instructeur',
			label: 'Lessen',
			icon: 'horse',
			iconFill: 'horse-fill',
			end: true,
			dot: badge,
		},
		{
			to: '/stal/instructeur/voortgang',
			label: 'Voortgang',
			icon: 'chart-line-up',
			iconFill: 'chart-line-up-fill',
		},
	]
}

export function InstructeurChrome({
	shell,
	children,
}: {
	shell: InstructeurShell
	children: React.ReactNode
}) {
	const items = tabs(shell.badge)
	return (
		<div className="st-shell st-ins">
			<header className="st-head">
				<Link
					to="/stal/instructeur"
					className="st-brand"
					aria-label={`Lessen ${shell.eName}`}
				>
					<span className="oy-stal-brand" aria-hidden="true">
						🐴
					</span>
					<b>Lessen {shell.eName}</b>
				</Link>
				<nav aria-label="Hoofdnavigatie" className="st-topnav">
					{items.map((t) => (
						<NavLink key={t.to} to={t.to} end={t.end}>
							{({ isActive }) => (
								<>
									<Icon
										name={isActive && t.iconFill ? t.iconFill : t.icon}
										className="st-topnav-ico"
										aria-hidden="true"
									/>
									{t.label}
									<TabMark item={t} />
								</>
							)}
						</NavLink>
					))}
				</nav>
				<span className="oy-app-avatarbtn" aria-label={shell.firstName}>
					<OyAvatar name={shell.firstName} hue={shell.hue} size={34} />
				</span>
			</header>
			{children}
			<OyTabBar items={items} label="Menu" />
		</div>
	)
}

// ── trays ───────────────────────────────────────────────────────────────────

export type NieuwData = {
	riders: Array<{ id: string; name: string }>
	horses: string[]
	dayChips: Array<{ dateKey: string; label: string }>
	times: string[]
	eName: string
}

export type InstructeurTrayState =
	| { kind: 'tijd'; check: InstructeurCheck; times: string[]; eName: string }
	| {
			kind: 'annuleer'
			check: InstructeurCheck
			dayChips: Array<{ dateKey: string; label: string }>
			times: string[]
			eName: string
			carerName: string
	  }
	| { kind: 'les'; lesson: InstructeurLessonRow; data: NieuwData }
	| { kind: 'nieuw'; data: NieuwData; todayKey: string }
	| {
			kind: 'afwezig'
			todayKey: string
			eName: string
			carerName: string
			other: string | null
	  }
	| { kind: 'log'; target: { id: string; label: string }; eName: string }

type TrayApi = { open: (t: InstructeurTrayState) => void; close: () => void }
const TrayContext = createContext<TrayApi | null>(null)

export function useInstructeurTray(): TrayApi {
	const api = useContext(TrayContext)
	if (!api)
		throw new Error('useInstructeurTray needs an InstructeurTrayProvider')
	return api
}

function useRetained<T>(value: T | null): T | null {
	const ref = useRef<T | null>(value)
	if (value !== null) ref.current = value
	return value ?? ref.current
}

export function InstructeurTrayProvider({
	shell,
	children,
}: {
	shell: InstructeurShell
	children: React.ReactNode
}) {
	const [state, setState] = useState<InstructeurTrayState | null>(null)
	const api = useMemo<TrayApi>(
		() => ({ open: (t) => setState(t), close: () => setState(null) }),
		[],
	)
	const shown = useRetained(state)
	const props = { shell, open: state !== null, onClose: api.close }
	let tray: React.ReactNode = null
	switch (shown?.kind) {
		case 'tijd':
			tray = <TijdTray key="tijd" {...props} state={shown} />
			break
		case 'annuleer':
			tray = <AnnuleerTray key="annuleer" {...props} state={shown} />
			break
		case 'les':
			tray = <LesTray key={`les:${shown.lesson.id}`} {...props} state={shown} />
			break
		case 'nieuw':
			tray = <NieuwTray key="nieuw" {...props} state={shown} />
			break
		case 'afwezig':
			tray = <AfwezigTray key="afwezig" {...props} state={shown} />
			break
		case 'log':
			tray = <LogTray key="log" {...props} state={shown} />
			break
	}
	return (
		<TrayContext.Provider value={api}>
			{children}
			{tray}
		</TrayContext.Provider>
	)
}

export type InstructeurResult =
	| { ok: true; toast: string; extra?: Record<string, unknown> }
	| { ok: false; error: string }

type TrayProps = { shell: InstructeurShell; open: boolean; onClose: () => void }

/** One fetcher per tray: post an intent, toast the result, close on ok. */
export function useInstructeurWrite(onDone?: () => void) {
	const fetcher = useFetcher<InstructeurResult>()
	const toast = useOyToast()
	const seen = useRef<InstructeurResult | null>(null)
	const [error, setError] = useState<string | null>(null)
	const cb = useRef(onDone)
	cb.current = onDone
	useEffect(() => {
		if (
			fetcher.state !== 'idle' ||
			!fetcher.data ||
			fetcher.data === seen.current
		)
			return
		seen.current = fetcher.data
		if (fetcher.data.ok) {
			setError(null)
			toast.say(fetcher.data.toast)
			cb.current?.()
		} else setError(fetcher.data.error)
	}, [fetcher.state, fetcher.data, toast])
	return {
		post: (fields: Record<string, string | undefined | null>) => {
			const body: Record<string, string> = {}
			for (const [k, v] of Object.entries(fields)) if (v != null) body[k] = v
			void fetcher.submit(body, {
				method: 'post',
				action: INSTRUCTEUR_DOORGEVEN,
			})
		},
		busy: fetcher.state !== 'idle',
		error,
		data: fetcher.data,
	}
}

function Status({ error, busy }: { error: string | null; busy: boolean }) {
	if (!error && !busy) return null
	return (
		<p className={error ? 'st-status warm' : 'st-status'} role="status">
			{error ?? 'Bezig…'}
		</p>
	)
}

/** The day-plan's own anchor sentence (packages/brain day-plan.ts) — never rephrased. */
export function anchorNl(time: string): string {
	return `"Als je om ${arrivalFor(time)} uur op stal bent, kunnen we rustig aan doen."`
}

function timeChips(times: string[], current: string | null): string[] {
	return current && !times.includes(current)
		? [current, ...times].sort()
		: times
}

function TijdTray({
	open,
	onClose,
	state,
}: TrayProps & { state: Extract<InstructeurTrayState, { kind: 'tijd' }> }) {
	const [time, setTime] = useState(state.check.time ?? state.times[0]!)
	const write = useInstructeurWrite(onClose)
	return (
		<OyTray
			open={open}
			onClose={onClose}
			icon="🕰"
			title="Andere tijd"
			intro={`Kies de nieuwe tijd. ${state.eName} krijgt haar aankomsttijd er automatisch bij.`}
			footer={{
				primary: {
					label: `Bevestig ${time}`,
					disabled: write.busy,
					onClick: () =>
						write.post({
							intent: 'les-tijd',
							lessonId: state.check.lessonId,
							startTime: time,
						}),
				},
			}}
		>
			<OyTrayField label="Tijd">
				{(id) => (
					<OyChipGroup mode="single" labelledBy={id}>
						{timeChips(state.times, state.check.time).map((t) => (
							<OyChip key={t} selected={time === t} onSelect={() => setTime(t)}>
								{t}
							</OyChip>
						))}
					</OyChipGroup>
				)}
			</OyTrayField>
			<OyTrayField label={`Voor ${state.eName}`}>
				<OyTrayNote>{anchorNl(time)}</OyTrayNote>
			</OyTrayField>
			<Status error={write.error} busy={write.busy} />
		</OyTray>
	)
}

export type AnnuleerRead = {
	kind: 'annuleer'
	moments: Array<{ dateKey: string; time: string; label: string }>
	preview: { ok: true; text: string } | { ok: false; consequence: string }
}

function AnnuleerTray({
	open,
	onClose,
	state,
}: TrayProps & { state: Extract<InstructeurTrayState, { kind: 'annuleer' }> }) {
	const reasons = [
		`te warm voor ${state.check.horse}`,
		'regen / bak nat',
		'ik ben ziek',
		`${state.check.horse} rust`,
		'anders',
	]
	const [reason, setReason] = useState<string | null>(null)
	const [moment, setMoment] = useState<string>('carer')
	const read = useFetcher<AnnuleerRead>()
	const write = useInstructeurWrite(onClose)
	useEffect(() => {
		void read.load(
			`${INSTRUCTEUR_DOORGEVEN}?annuleer=${encodeURIComponent(state.check.lessonId)}`,
		)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [state.check.lessonId])
	const moments = read.data?.moments ?? []
	const chosen = moments.find((m) => `${m.dateKey}T${m.time}` === moment)
	return (
		<OyTray
			open={open}
			onClose={onClose}
			icon="🌧"
			iconBg="var(--oy-warm-soft)"
			iconFg="var(--oy-warm)"
			title="Les gaat niet door"
			intro={`Zeg waarom. Oyka geeft het door aan ${state.carerName}; wat ${state.eName} hoort, blijft positief.`}
			footer={{
				primary: {
					label: 'Doorgeven',
					tone: 'warm',
					disabled: write.busy,
					onClick: () =>
						write.post({
							intent: 'les-vervalt',
							lessonId: state.check.lessonId,
							reason: reason ?? undefined,
							newDateKey: chosen?.dateKey,
							newTime: chosen?.time,
							letCarerChoose: moment === 'carer' ? 'true' : undefined,
						}),
				},
			}}
		>
			<OyTrayField label="Reden">
				{(id) => (
					<OyChipGroup mode="single" labelledBy={id}>
						{reasons.map((r) => (
							<OyChip
								key={r}
								selected={reason === r}
								onSelect={() => setReason(r)}
							>
								{r}
							</OyChip>
						))}
					</OyChipGroup>
				)}
			</OyTrayField>
			<OyTrayField label="Nieuw moment">
				{(id) => (
					<OyChipGroup mode="single" labelledBy={id}>
						{moments.map((m) => (
							<OyChip
								key={`${m.dateKey}T${m.time}`}
								selected={moment === `${m.dateKey}T${m.time}`}
								onSelect={() => setMoment(`${m.dateKey}T${m.time}`)}
							>
								{m.label}
							</OyChip>
						))}
						<OyChip
							selected={moment === 'carer'}
							onSelect={() => setMoment('carer')}
						>
							laat {state.carerName} kiezen
						</OyChip>
					</OyChipGroup>
				)}
			</OyTrayField>
			<OyTrayField
				label={
					read.data?.preview.ok ? `${state.eName} krijgt` : 'Wat er gebeurt'
				}
			>
				{read.data ? (
					read.data.preview.ok ? (
						<OyTrayNote tone="muted">"{read.data.preview.text}"</OyTrayNote>
					) : (
						<OyTrayNote tone="warm">{read.data.preview.consequence}</OyTrayNote>
					)
				) : (
					<OyTrayNote tone="muted">even kijken…</OyTrayNote>
				)}
			</OyTrayField>
			<Status error={write.error} busy={write.busy} />
		</OyTray>
	)
}

const KINDS: Array<{ key: LessonKind; label: string }> = [
	{ key: 'les', label: 'eigen les' },
	{ key: 'aansluitend', label: 'aansluitend op de vorige' },
	{ key: 'gedeeld', label: 'gedeelde les (60 min)' },
]

function LesTray({
	open,
	onClose,
	state,
}: TrayProps & { state: Extract<InstructeurTrayState, { kind: 'les' }> }) {
	const { lesson, data } = state
	const [time, setTime] = useState(lesson.time ?? data.times[0]!)
	const [horse, setHorse] = useState(lesson.horse)
	const [kind, setKind] = useState<LessonKind>(lesson.kind)
	const write = useInstructeurWrite(onClose)
	const intro = `${lesson.horse}, ${lesson.durationMin} min${lesson.kind === 'gedeeld' ? ' gedeeld' : ''}. ${
		lesson.isElse
			? lesson.status === 'bevestigd'
				? `Bevestigd. ${data.eName} hoort het om 08:00.`
				: lesson.canConfirm
					? 'Wordt de dag ervoor om 18:00 gecheckt, of bevestig nu.'
					: `Wordt de dag ervoor om 18:00 met je gecheckt; ${data.eName} krijgt dan haar aankomsttijd.`
			: 'Eigen les. Oyka meldt het alleen aan de ruiter zelf.'
	}`
	const changed =
		time !== lesson.time || horse !== lesson.horse || kind !== lesson.kind
	return (
		<OyTray
			open={open}
			onClose={onClose}
			icon="🏇"
			title={`${lesson.who}, ${lesson.dow} ${lesson.num}`}
			intro={intro}
			footer={{
				primary: {
					label: lesson.canConfirm ? `Bevestig ${time}` : 'Opslaan',
					disabled: write.busy || (!lesson.canConfirm && !changed),
					onClick: () =>
						write.post({
							intent: 'les-save',
							lessonId: lesson.id,
							startTime: time,
							horse,
							kind,
							confirm: lesson.canConfirm ? 'true' : undefined,
						}),
				},
			}}
		>
			<OyTrayField label="Tijd">
				{(id) => (
					<OyChipGroup mode="single" labelledBy={id}>
						{timeChips(data.times, lesson.time).map((t) => (
							<OyChip key={t} selected={time === t} onSelect={() => setTime(t)}>
								{t}
							</OyChip>
						))}
					</OyChipGroup>
				)}
			</OyTrayField>
			<OyTrayField label="Ruiter(s)">
				{(id) => (
					<OyChipGroup mode="multi" labelledBy={id}>
						{data.riders.map((r) => (
							<OyChip
								key={r.id}
								selected={
									r.id === lesson.riderId || r.id === lesson.sharedWithId
								}
								disabled
								onSelect={() => {}}
							>
								{r.name}
							</OyChip>
						))}
					</OyChipGroup>
				)}
			</OyTrayField>
			<OyTrayField label="Vorm">
				{(id) => (
					<OyChipGroup mode="single" labelledBy={id}>
						{KINDS.map((k) => (
							<OyChip
								key={k.key}
								selected={kind === k.key}
								onSelect={() => setKind(k.key)}
							>
								{k.label}
							</OyChip>
						))}
					</OyChipGroup>
				)}
			</OyTrayField>
			<OyTrayField label="Paard">
				{(id) => (
					<OyChipGroup mode="single" labelledBy={id}>
						{data.horses.map((h) => (
							<OyChip
								key={h}
								selected={horse === h}
								onSelect={() => setHorse(h)}
							>
								{h}
							</OyChip>
						))}
					</OyChipGroup>
				)}
			</OyTrayField>
			{lesson.isElse ? (
				<OyTrayField label={`Voor ${data.eName}`}>
					<OyTrayNote>
						Aankomst {ARRIVAL_LEAD_MIN} min eerder: {anchorNl(time)}. De tekst
						voor haar maakt Oyka zelf, pas na je bevestiging.
					</OyTrayNote>
				</OyTrayField>
			) : null}
			<Status error={write.error} busy={write.busy} />
		</OyTray>
	)
}

function NieuwTray({
	open,
	onClose,
	state,
}: TrayProps & { state: Extract<InstructeurTrayState, { kind: 'nieuw' }> }) {
	const { data } = state
	const options = [
		...data.riders.map((r) => ({
			key: r.id,
			label: r.name,
			riderId: r.id,
			sharedWithId: null as string | null,
		})),
		...data.riders
			.filter((r) => r.id !== 'E')
			.map((r) => ({
				key: `E+${r.id}`,
				label: `${data.eName} + ${r.name}`,
				riderId: 'E',
				sharedWithId: r.id as string | null,
			})),
	]
	const [who, setWho] = useState(options[0]!.key)
	const [day, setDay] = useState(data.dayChips[0]?.dateKey ?? state.todayKey)
	const [time, setTime] = useState(data.times[0]!)
	const [horse, setHorse] = useState(data.horses[0] ?? '')
	const write = useInstructeurWrite(onClose)
	const chosen = options.find((o) => o.key === who)!
	const dayLabel = data.dayChips.find((d) => d.dateKey === day)?.label ?? day
	return (
		<OyTray
			open={open}
			onClose={onClose}
			icon="＋"
			title="Les toevoegen"
			intro={`Voor ${data.eName} checkt Oyka de dag ervoor of er iemand bij is; voor anderen alleen of de bak vrij is.`}
			footer={{
				primary: {
					label: horse
						? `${chosen.label}, ${dayLabel} ${time}`
						: 'Kies eerst een paard',
					disabled: write.busy || !horse,
					onClick: () =>
						write.post({
							intent: 'les-nieuw',
							dateKey: day,
							startTime: time,
							riderId: chosen.riderId,
							sharedWithId: chosen.sharedWithId ?? undefined,
							horse,
						}),
				},
			}}
		>
			<OyTrayField label="Ruiter(s)">
				{(id) => (
					<OyChipGroup mode="single" labelledBy={id}>
						{options.map((o) => (
							<OyChip
								key={o.key}
								selected={who === o.key}
								onSelect={() => setWho(o.key)}
							>
								{o.label}
							</OyChip>
						))}
					</OyChipGroup>
				)}
			</OyTrayField>
			<OyTrayField label="Dag">
				{(id) => (
					<OyChipGroup mode="single" labelledBy={id}>
						{data.dayChips.map((d) => (
							<OyChip
								key={d.dateKey}
								selected={day === d.dateKey}
								onSelect={() => setDay(d.dateKey)}
							>
								{d.label}
							</OyChip>
						))}
					</OyChipGroup>
				)}
			</OyTrayField>
			<OyTrayField label="Tijd">
				{(id) => (
					<OyChipGroup mode="single" labelledBy={id}>
						{data.times.map((t) => (
							<OyChip key={t} selected={time === t} onSelect={() => setTime(t)}>
								{t}
							</OyChip>
						))}
					</OyChipGroup>
				)}
			</OyTrayField>
			<OyTrayField label="Paard">
				{(id) => (
					<OyChipGroup mode="single" labelledBy={id}>
						{data.horses.map((h) => (
							<OyChip
								key={h}
								selected={horse === h}
								onSelect={() => setHorse(h)}
							>
								{h}
							</OyChip>
						))}
					</OyChipGroup>
				)}
			</OyTrayField>
			<Status error={write.error} busy={write.busy} />
		</OyTray>
	)
}

const SPANS = [
	{ label: 'één dag', days: 1 },
	{ label: 'een week', days: 7 },
	{ label: 'twee weken', days: 14 },
	{ label: 'langer', days: 28 },
]
const DAY_SHORT = new Intl.DateTimeFormat('nl-NL', {
	timeZone: 'Europe/Amsterdam',
	weekday: 'short',
	day: 'numeric',
})
function nextMonday(fromKey: string, weeks: number): string {
	const dow = new Date(`${fromKey}T12:00:00Z`).getUTCDay()
	const toMonday = (8 - dow) % 7 || 7
	return addDays(fromKey, toMonday + (weeks - 1) * 7)
}

function AfwezigTray({
	open,
	onClose,
	state,
}: TrayProps & { state: Extract<InstructeurTrayState, { kind: 'afwezig' }> }) {
	const starts = [
		{ key: addDays(state.todayKey, 1), label: 'morgen' },
		{
			key: nextMonday(state.todayKey, 1),
			label: DAY_SHORT.format(
				new Date(`${nextMonday(state.todayKey, 1)}T12:00:00Z`),
			),
		},
		{
			key: nextMonday(state.todayKey, 2),
			label: DAY_SHORT.format(
				new Date(`${nextMonday(state.todayKey, 2)}T12:00:00Z`),
			),
		},
	]
	const [span, setSpan] = useState(7)
	const [from, setFrom] = useState(starts[0]!.key)
	const write = useInstructeurWrite(onClose)
	return (
		<OyTray
			open={open}
			onClose={onClose}
			icon="🏖"
			title="Ik ben er niet"
			intro={
				state.other
					? `Oyka zet je lessen op "wie?" en vraagt ${state.other} om over te nemen; ${state.carerName} hoort het.`
					: `Oyka zet je lessen op "wie?"; ${state.carerName} hoort het en regelt een vervanger.`
			}
			footer={{
				primary: {
					label: 'Doorgeven',
					disabled: write.busy,
					onClick: () =>
						write.post({
							intent: 'afwezig',
							from,
							to: addDays(from, span - 1),
						}),
				},
			}}
		>
			<OyTrayField label="Hoe lang">
				{(id) => (
					<OyChipGroup mode="single" labelledBy={id}>
						{SPANS.map((s) => (
							<OyChip
								key={s.label}
								selected={span === s.days}
								onSelect={() => setSpan(s.days)}
							>
								{s.label}
							</OyChip>
						))}
					</OyChipGroup>
				)}
			</OyTrayField>
			<OyTrayField label="Vanaf">
				{(id) => (
					<OyChipGroup mode="single" labelledBy={id}>
						{starts.map((s) => (
							<OyChip
								key={s.key}
								selected={from === s.key}
								onSelect={() => setFrom(s.key)}
							>
								{s.label}
							</OyChip>
						))}
					</OyChipGroup>
				)}
			</OyTrayField>
			<Status error={write.error} busy={write.busy} />
		</OyTray>
	)
}

const INSIGHT_KINDS = [
	{ key: 'werkt', label: 'wat werkt' },
	{ key: 'beter', label: 'kan beter' },
	{ key: 'idee', label: 'idee' },
] as const

function LogTray({
	open,
	onClose,
	state,
}: TrayProps & { state: Extract<InstructeurTrayState, { kind: 'log' }> }) {
	const [mood, setMood] = useState<string>(MOODS[1])
	const [text, setText] = useState('')
	const [tags, setTags] = useState<string[]>([])
	const [kind, setKind] = useState<'werkt' | 'beter' | 'idee'>('werkt')
	const write = useInstructeurWrite(onClose)
	const ready = text.trim().length > 0
	return (
		<OyTray
			open={open}
			onClose={onClose}
			icon="📝"
			title="Notitie na de les"
			intro={`${state.target.label}. Eén zin is genoeg. Dit helpt de kring om ${state.eName} te ondersteunen.`}
			footer={{
				primary: ready
					? {
							label: 'Bewaren',
							disabled: write.busy,
							onClick: () =>
								write.post({
									intent: 'notitie',
									lessonId: state.target.id,
									mood,
									text,
									tags: tags.join(','),
									kind,
								}),
						}
					: { label: 'Schrijf een zin', disabled: true },
			}}
		>
			<OyTrayField label="Hoe ging het">
				{(id) => (
					<OyChipGroup mode="single" labelledBy={id}>
						{MOODS.map((m) => (
							<OyChip key={m} selected={mood === m} onSelect={() => setMood(m)}>
								{m}
							</OyChip>
						))}
					</OyChipGroup>
				)}
			</OyTrayField>
			<OyTrayField label="Notitie">
				<OyField
					value={text}
					onChange={(e) => setText(e.target.value)}
					placeholder="bijv. mooie overgangen, wist zelf de tijd nog"
					maxLength={400}
				/>
			</OyTrayField>
			<OyTrayField label="Wat werkte">
				{(id) => (
					<OyChipGroup mode="multi" labelledBy={id}>
						{NOTE_TAGS.map((t) => (
							<OyChip
								key={t}
								selected={tags.includes(t)}
								onSelect={() =>
									setTags((cur) =>
										cur.includes(t) ? cur.filter((x) => x !== t) : [...cur, t],
									)
								}
							>
								{t}
							</OyChip>
						))}
					</OyChipGroup>
				)}
			</OyTrayField>
			<OyTrayField label="Voor de kring als">
				{(id) => (
					<OyChipGroup mode="single" labelledBy={id}>
						{INSIGHT_KINDS.map((k) => (
							<OyChip
								key={k.key}
								selected={kind === k.key}
								onSelect={() => setKind(k.key)}
							>
								{k.label}
							</OyChip>
						))}
					</OyChipGroup>
				)}
			</OyTrayField>
			<Status error={write.error} busy={write.busy} />
		</OyTray>
	)
}

/** A lesson row (StalAppRollen ins.lessons): day, time, rider pill, horse, state, ⇄. */
export function InstructeurLessonRowView({
	l,
	highlight,
	onOpen,
}: {
	l: InstructeurLessonRow
	highlight?: boolean
	onOpen: () => void
}) {
	return (
		<button
			type="button"
			className={cn('st-ins-lesson', highlight && 'check')}
			onClick={onOpen}
		>
			<span className={cn('date', !l.showDay && 'dim')}>
				{l.dow}
				<b>{l.num}</b>
			</span>
			<span className="body">
				<span className="line">
					<span className="time">{l.time ?? 'tijd volgt'}</span>
					<span className={cn('who', l.isElse && 'else')}>{l.who}</span>
					<span className="horse">{l.horse}</span>
				</span>
				<span className="pills">
					<span className={cn('oy-pill', `oy-pill-${l.tone}`)}>{l.state}</span>
					{l.link ? <span className="link">⇄ {l.link}</span> : null}
				</span>
			</span>
			<span className="chev" aria-hidden="true">
				›
			</span>
		</button>
	)
}
